import { Node, mergeAttributes } from "@tiptap/core";
import { PRICING_TABLE_INCLUDE, isPricingTableText } from "./pricing_table_include.js";

// Opaque block node for the pricing table. Authors insert it from the toolbar;
// it renders an uneditable card in the editor and serializes to the literal
// `{% include "pricing_table" %}` tag, which the server renders via Liquid.
//
// Why a node and not a hand-built looped <table>: ProseMirror's table schema
// only allows rows/cells inside a table, so loose `{% for %}` text between rows
// is evicted to a paragraph on every getHTML() round-trip, breaking the loop.
// An atom block is never loose table text, so it round-trips losslessly.
export const PricingTableInclude = Node.create({
  name: "pricingTable",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return { label: "Pricing table" };
  },

  parseHTML() {
    return [
      // The marker this node serializes to.
      { tag: 'div[data-liquid-include="pricing_table"]' },
      // Reconstitute a bare-text include (AI-generated or hand-typed) into the
      // node so it gets the friendly card and a stable serialization.
      {
        tag: "p",
        getAttrs: (el) => (isPricingTableText(el.textContent) ? {} : false)
      }
    ];
  },

  renderHTML() {
    // -> <div data-liquid-include="pricing_table" class="liquid-pricing-table">{% include "pricing_table" %}</div>
    // The include text is rendered server-side by Liquid; the wrapper div is
    // inert in the PDF and is what parseHTML matches on reload. The tag text is
    // a DOM text node, so its quotes are not HTML-escaped.
    return [
      "div",
      mergeAttributes({ "data-liquid-include": "pricing_table", class: "liquid-pricing-table" }),
      PRICING_TABLE_INCLUDE
    ];
  },

  addNodeView() {
    const label = this.options.label;
    return () => {
      const dom = document.createElement("div");
      dom.className =
        "liquid-pricing-table-card my-2 flex items-center gap-2 rounded-lg border border-dashed " +
        "border-primary-400 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-800 " +
        "dark:border-primary-600 dark:bg-primary-900/20 dark:text-primary-300";
      dom.setAttribute("contenteditable", "false");
      dom.textContent = label;
      return { dom };
    };
  }
});
